<script setup lang="ts">
import MapControlPanel from '@/components/_common/navbar/MapControlPanel.vue';
import GeofenceMap from '@/components/geofence/map/GeofenceMap.vue';
import GeofenceForm from '@/components/geofence/_common/GeofenceForm.vue';
import GeofenceDetailsNavBar from '@/components/geofence/details/GeofenceDetailsNavBar.vue';
import ThreeColumnLayout from "@/components/_common/ThreeColumnLayout.vue";

import { useSynchronizedSidebars } from "@/composables/useSynchronizedSidebars";
useSynchronizedSidebars({ defaultOpenSidebar: 'left' });

import {useResetChatOnSidebarClose} from "@/composables/useResetChatOnSidebarClose";
useResetChatOnSidebarClose();

import {useResetChatOnAccountOrUserChange} from "@/composables/useResetChatOnAccountOrUserChange";
useResetChatOnAccountOrUserChange();

import {computed, onMounted, watch, ref} from "vue";
import {useStore} from "vuex";
import {setTitle} from "@/utils/title";
import NProgress from 'nprogress';
import GeofenceDetailsBar from "@/components/geofence/details/GeofenceDetailsBar.vue";
import {useGeofence} from "@/composables/useGeofence";

// Use composable for geofence data
const { geofence, isLoading } = useGeofence();
const store = useStore();
const isB2B = store.getters['auth/isB2B'];

// Key for forcing GeofenceForm to re-render when geofence data changes
const geofenceFormKey = ref(0);

// Computed key for GeofenceForm to safely handle undefined geofence
const geofenceFormKeyValue = computed(() => {
  const geofenceId = geofence.value?.id || 0;
  return `geofence-form-${geofenceId}-${geofenceFormKey.value}`;
});

// Reactive initial values that update when geofence changes
const initialValues = computed(() => {
  if (!geofence.value) {
    return {
      address: {
        label: '',
        lat: 1,
        lng: 1,
      },
      is_alertable: false,
    };
  }
  
  return {
    ...geofence.value,
    address: {
      label: geofence.value.address || '',
      lat: geofence.value.lat || 1,
      lng: geofence.value.lng || 1,
    },
    is_alertable: !!geofence.value.is_alertable,
  };
});

// Watch for geofence changes to force GeofenceForm re-render
// This ensures the form component is completely recreated with new initial values
watch(
  () => geofence.value,
  (newGeofence, oldGeofence) => {
    // Only increment key if geofence actually changed (not just initial load)
    if (newGeofence && oldGeofence) {
      // Increment key to force component re-render when geofence data changes
      geofenceFormKey.value += 1;
    }
  },
  { deep: true }
);

setTitle('Geofence');

// Update title when geofence name changes
watch(
  () => geofence.value?.name,
  (name) => {
    if (name) {
      setTitle(`Geofence ${name}`);
    }
  },
  { immediate: true }
);

// Show/hide NProgress based on loading state
watch(isLoading, (isLoadingValue) => {
  if (isLoadingValue) {
    NProgress.start();
  } else {
    NProgress.done();
  }
}, { immediate: true });
</script>


<template>
  <ThreeColumnLayout>
    <template #leftTop>
      <GeofenceDetailsNavBar />
    </template>
    <template #left>
      <div class="content-scrollable min-h-0">
        <GeofenceForm
          :key="geofenceFormKeyValue"
          :initial-values="initialValues"
          :is-b2b="isB2B"
          readonly
        />
      </div>
    </template>
    <template #rightTop>
      <MapControlPanel>
        <template #filters>
          <GeofenceDetailsBar />
        </template>
      </MapControlPanel>
    </template>
    <template #right>
      <GeofenceMap view="single" />
    </template>
  </ThreeColumnLayout>
</template>
